import { useEffect, useState } from "react";
import { Upload, Loader2, Plus, Trash2, X, FileText, FileCheck2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { compressImage, dataUrlBytes, formatBytes } from "../lib/image";
import { readPdfAsDataUrl } from "../lib/pdf";
import { toast } from "sonner";

const EMPTY = {
  category_id: "",
  subcategory_id: "",
  name: "",
  brand: "",
  sku: "",
  short_description: "",
  description: "",
  image: null,
  tech_sheet_pdf: null,
  tech_sheet_filename: "",
  specs: [],
  is_active: true,
};

export default function ProductForm({
  initial,
  categories,
  subcategories,
  onSubmit,
  onCancel,
  saving,
}) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [imgLoading, setImgLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    setForm({ ...EMPTY, ...initial });
  }, [initial]);

  const subs = subcategories.filter((s) => s.category_id === form.category_id);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("La imagen es muy grande (máx 8 MB).");
      return;
    }
    try {
      setImgLoading(true);
      const dataUrl = await compressImage(file, 1000, 0.82);
      set("image", dataUrl);
    } catch (err) {
      toast.error("No se pudo procesar la imagen", {
        description: String(err.message || err),
      });
    } finally {
      setImgLoading(false);
    }
  };

  const onPickPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPdfLoading(true);
      const dataUrl = await readPdfAsDataUrl(file, 7);
      setForm((f) => ({
        ...f,
        tech_sheet_pdf: dataUrl,
        tech_sheet_filename: file.name,
      }));
      toast.success("PDF cargado", { description: file.name });
    } catch (err) {
      toast.error("No se pudo cargar el PDF", {
        description: String(err.message || err),
      });
    } finally {
      setPdfLoading(false);
      e.target.value = "";
    }
  };

  const addSpec = () =>
    set("specs", [...form.specs, { label: "", value: "" }]);
  const updateSpec = (i, k, v) => {
    const next = [...form.specs];
    next[i] = { ...next[i], [k]: v };
    set("specs", next);
  };
  const removeSpec = (i) =>
    set(
      "specs",
      form.specs.filter((_, idx) => idx !== i)
    );

  const submit = (e) => {
    e.preventDefault();
    if (!form.category_id || !form.name) {
      toast.error("Categoría y nombre son obligatorios.");
      return;
    }
    const cleanSpecs = form.specs.filter(
      (s) => s.label?.trim() && s.value?.trim()
    );
    onSubmit({
      ...form,
      subcategory_id: form.subcategory_id || null,
      specs: cleanSpecs,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="product-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-slate-700">Categoría *</Label>
          <Select
            value={form.category_id}
            onValueChange={(v) => {
              set("category_id", v);
              set("subcategory_id", "");
            }}
          >
            <SelectTrigger
              data-testid="product-category"
              className="mt-1.5 h-11 rounded-sm border-slate-300"
            >
              <SelectValue placeholder="Seleccione categoría" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-700">Subcategoría</Label>
          <Select
            value={form.subcategory_id || ""}
            onValueChange={(v) => set("subcategory_id", v)}
            disabled={!form.category_id || subs.length === 0}
          >
            <SelectTrigger
              data-testid="product-subcategory"
              className="mt-1.5 h-11 rounded-sm border-slate-300"
            >
              <SelectValue
                placeholder={
                  subs.length === 0 ? "Sin subcategorías" : "Seleccione subcategoría"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {subs.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-slate-700">Nombre *</Label>
          <Input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="product-name"
            placeholder="Ej: Guante anticorte HyFlex 11-738"
            className="mt-1.5 h-11 rounded-sm border-slate-300"
          />
        </div>
        <div>
          <Label className="text-slate-700">Marca</Label>
          <Input
            value={form.brand || ""}
            onChange={(e) => set("brand", e.target.value)}
            data-testid="product-brand"
            placeholder="Ansell, 3M, Dräger..."
            className="mt-1.5 h-11 rounded-sm border-slate-300"
          />
        </div>
        <div>
          <Label className="text-slate-700">Código SKU</Label>
          <Input
            value={form.sku || ""}
            onChange={(e) => set("sku", e.target.value)}
            data-testid="product-sku"
            placeholder="HYF-11738-M"
            className="mt-1.5 h-11 rounded-sm border-slate-300"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-slate-700">Descripción corta</Label>
          <Input
            value={form.short_description || ""}
            onChange={(e) => set("short_description", e.target.value)}
            data-testid="product-short-desc"
            placeholder="1 línea para mostrar en la grilla del catálogo"
            className="mt-1.5 h-11 rounded-sm border-slate-300"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-slate-700">Descripción completa</Label>
          <Textarea
            value={form.description || ""}
            onChange={(e) => set("description", e.target.value)}
            data-testid="product-desc"
            rows={4}
            placeholder="Detalle del producto, usos, ventajas..."
            className="mt-1.5 rounded-sm border-slate-300"
          />
        </div>
      </div>

      {/* Image */}
      <div>
        <Label className="text-slate-700">Foto del producto</Label>
        <div className="mt-2 flex items-center gap-4">
          <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-sm overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">
            {imgLoading ? (
              <Loader2 className="animate-spin text-slate-400" size={24} />
            ) : form.image ? (
              <img
                src={form.image}
                alt="preview"
                className="w-full h-full object-contain"
              />
            ) : (
              <Upload className="text-slate-400" size={24} />
            )}
          </div>
          <div className="flex-1">
            <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-pv-green hover:bg-pv-green/90 text-white rounded-sm cursor-pointer text-sm font-semibold transition-colors">
              <Upload size={16} />
              Subir imagen
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPick}
                data-testid="product-image-input"
              />
            </label>
            {form.image && (
              <button
                type="button"
                onClick={() => set("image", null)}
                className="ml-2 text-sm text-slate-500 hover:text-red-600"
              >
                Quitar
              </button>
            )}
            <p className="text-xs text-slate-500 mt-2">
              JPG/PNG · Se redimensiona automáticamente a 1000px máx ·{" "}
              {form.image && `Tamaño: ${formatBytes(dataUrlBytes(form.image))}`}
            </p>
          </div>
        </div>
      </div>

      {/* PDF technical sheet */}
      <div>
        <Label className="text-slate-700">Ficha técnica (PDF)</Label>
        <p className="text-xs text-slate-500 mt-1 mb-2">
          Opcional. Si subís un PDF, los visitantes podrán descargarlo desde la
          ficha del producto.
        </p>
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center gap-3 flex-1 px-4 py-3 border-2 border-dashed rounded-sm ${
              form.tech_sheet_pdf
                ? "border-pv-green bg-pv-green/5"
                : "border-slate-300 bg-slate-50"
            }`}
          >
            {pdfLoading ? (
              <Loader2 className="animate-spin text-slate-400" size={20} />
            ) : form.tech_sheet_pdf ? (
              <FileCheck2 className="text-pv-green shrink-0" size={20} />
            ) : (
              <FileText className="text-slate-400 shrink-0" size={20} />
            )}
            <div className="flex-1 min-w-0">
              {form.tech_sheet_pdf ? (
                <>
                  <div className="text-sm font-medium text-pv-green truncate">
                    {form.tech_sheet_filename || "ficha-tecnica.pdf"}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatBytes(dataUrlBytes(form.tech_sheet_pdf))} · Listo para guardar
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-500">
                  Ningún PDF cargado
                </div>
              )}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-pv-green hover:bg-pv-green/90 text-white rounded-sm cursor-pointer text-sm font-semibold transition-colors shrink-0">
            <Upload size={16} />
            {form.tech_sheet_pdf ? "Reemplazar" : "Subir PDF"}
            <input
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={onPickPdf}
              data-testid="product-pdf-input"
            />
          </label>
          {form.tech_sheet_pdf && (
            <button
              type="button"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  tech_sheet_pdf: null,
                  tech_sheet_filename: "",
                }))
              }
              className="text-sm text-slate-500 hover:text-red-600 px-2"
              data-testid="remove-pdf"
            >
              Quitar
            </button>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Máx. 7 MB · Solo PDF
        </p>
      </div>

      {/* Tech specs */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-slate-700">Ficha técnica</Label>
          <Button
            type="button"
            onClick={addSpec}
            variant="outline"
            size="sm"
            data-testid="add-spec"
            className="rounded-sm"
          >
            <Plus size={14} className="mr-1" /> Agregar
          </Button>
        </div>
        {form.specs.length === 0 && (
          <p className="text-xs text-slate-500 italic">
            Agregue normas, materiales, tallas, colores, etc.
          </p>
        )}
        <div className="space-y-2">
          {form.specs.map((s, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={s.label}
                onChange={(e) => updateSpec(i, "label", e.target.value)}
                placeholder="Etiqueta (ej: Norma)"
                data-testid={`spec-label-${i}`}
                className="rounded-sm border-slate-300 sm:max-w-xs"
              />
              <Input
                value={s.value}
                onChange={(e) => updateSpec(i, "value", e.target.value)}
                placeholder="Valor (ej: EN388:2016 nivel D)"
                data-testid={`spec-value-${i}`}
                className="rounded-sm border-slate-300 flex-1"
              />
              <button
                type="button"
                onClick={() => removeSpec(i)}
                className="px-3 text-slate-400 hover:text-red-600"
                aria-label="Eliminar"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="is_active"
          checked={form.is_active}
          onChange={(e) => set("is_active", e.target.checked)}
          data-testid="product-active"
        />
        <Label htmlFor="is_active" className="text-slate-700">
          Producto visible en el catálogo público
        </Label>
      </div>

      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Button
          type="submit"
          disabled={saving}
          data-testid="product-save"
          className="rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white h-11 px-6 font-semibold"
        >
          {saving ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            "Guardar producto"
          )}
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          variant="outline"
          className="rounded-sm h-11 px-6"
          data-testid="product-cancel"
        >
          <X size={16} className="mr-1" /> Cancelar
        </Button>
      </div>
    </form>
  );
}
